import { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { tagButtonStyle } from "@/components/FinancialShared/tableStyles";
import { IRelatorioSituacaoParcelasRetorno } from "@/models/consignado/proposal-billings.model";
import { gerarRelatorioSituacaoParcelas } from "@/services/consignado/proposta-cobranca.service";
import { formatDateTimeWithAt } from "@/utils/dateFormatter";
import { formatDateWithNoHours } from "@/utils/formatDateWithNoHours";
import { formLabelProps, modalHeaderProps } from "./modalStyles";
import RelatorioGeradoCard from "./RelatorioGeradoCard";
import { useApiErrorToast } from "@/hooks/useApiErrorToast";

// Modal "Situação das Parcelas" da seção Parcelas — REAL (TAR-ADM-cobranca-041).
// Período opcional em dois inputs de data (mesmo padrão visual da "Data de
// cálculo" do Relatório de Quitação) → GET .../relatorio-situacao-parcelas →
// abre o PDF devolvido. O "Relatório de Quitação" mora em RelatorioQuitacaoModal.tsx.

type SituacaoParcelasProps = {
  isOpen: boolean;
  onClose: () => void;
  numeroCcb: string;
};

export function SituacaoParcelasModal({ isOpen, onClose, numeroCcb }: SituacaoParcelasProps) {
  const toastErro = useApiErrorToast();
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");

  const gerar = useMutation({
    mutationFn: () =>
      gerarRelatorioSituacaoParcelas(numeroCcb, inicio || undefined, fim || undefined),
    onSuccess(relatorio) {
      window.open(relatorio.url, "_blank", "noopener");
    },
    onError: toastErro("Erro ao gerar relatório"),
  });

  const handleClose = () => {
    setInicio("");
    setFim("");
    gerar.reset();
    onClose();
  };

  const periodoInvalido = Boolean(inicio && fim && fim < inicio);

  const relatorio: IRelatorioSituacaoParcelasRetorno | undefined = gerar.data;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="lg" boxShadow="2xl">
        <ModalHeader {...modalHeaderProps}>Situação das Parcelas</ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={6}>
          <Flex direction="column" gap={5} fontFamily="Lexend Deca">
            <Box>
              <Text fontSize="sm" fontWeight="500" color="gray.700" mb={2}>
                Período (vencimento das parcelas)
              </Text>
              <Flex gap={4} direction={{ base: "column", md: "row" }}>
                <FormControl>
                  <FormLabel {...formLabelProps}>Início</FormLabel>
                  <Input
                    type="date"
                    size="sm"
                    maxW="200px"
                    value={inicio}
                    max={fim || undefined}
                    onChange={(e) => setInicio(e.target.value)}
                  />
                </FormControl>
                <FormControl isInvalid={periodoInvalido}>
                  <FormLabel {...formLabelProps}>Fim</FormLabel>
                  <Input
                    type="date"
                    size="sm"
                    maxW="200px"
                    value={fim}
                    min={inicio || undefined}
                    onChange={(e) => setFim(e.target.value)}
                  />
                </FormControl>
              </Flex>
              <Text fontSize="xs" color="gray.500" mt={2}>
                Opcional — sem período, o relatório traz todas as parcelas da CCB {numeroCcb}.
              </Text>
            </Box>

            {relatorio && (
              <RelatorioGeradoCard nomeArquivo={relatorio.nome_arquivo} url={relatorio.url}>
                <Text color="gray.600" fontSize="xs">
                  {relatorio.quantidade_parcelas} parcela{relatorio.quantidade_parcelas === 1 ? "" : "s"}
                  {relatorio.periodo.inicio || relatorio.periodo.fim
                    ? ` · período ${relatorio.periodo.inicio ? formatDateWithNoHours(relatorio.periodo.inicio) : "…"} a ${relatorio.periodo.fim ? formatDateWithNoHours(relatorio.periodo.fim) : "…"}`
                    : " · sem filtro de período"}
                </Text>
                <Text color="gray.600" fontSize="xs">
                  Dados de {formatDateTimeWithAt(new Date(relatorio.referencia_dados))} · gerado em{" "}
                  {formatDateTimeWithAt(new Date(relatorio.gerado_em))}
                </Text>
              </RelatorioGeradoCard>
            )}
          </Flex>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderTopColor="gray.200" pt={4}>
          <Button
            {...tagButtonStyle("blue")}
            fontFamily="Lexend Deca"
            onClick={() => gerar.mutate()}
            isLoading={gerar.isPending}
            isDisabled={periodoInvalido}
            loadingText="Gerando..."
          >
            Exportar relatório
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
